import { useCallback, useEffect, useRef } from 'react';

type DeferredPromise<DeferType> = {
  resolve: (value: DeferType) => void;
  reject: (value: unknown) => void;
  promise: Promise<DeferType>;
};

/**
 * A way to defer a promise so you can resolve it in separate functions.
 * This is handy when you need to present a confirm type action to the user but want to await
 * for that confirmation before carrying on.
 * Some reading on what this is: https://dev.to/vicnovais/creating-a-deferred-promise-hook-in-react-39jh
 *
 * @example
 * const [show, setShow] = useState(false);
 * const { defer, deferRef } = useDeferredPromise<boolean>();
 *
 * const showConfirm = () => {
 *   setShow(true);
 *   // here we wait for the user, but we don't want to perform any actions until it's done
 *   return defer().promise;
 * }
 *
 * const onConfirmed = () => {
 *   setShow(false)
 *   // the user confirmed the action, we resolve the deferred promise:
 *   // The resolve returns "true" as that's the type of deferred we created (see the type above)
 *   // Returning true/false or any other object is useful for the `const result = await showConfirm();`
 *   deferRef.current?.resolve(true);
 * }
 */
export function useDeferredPromise<DeferType>() {
  const deferRef = useRef<DeferredPromise<DeferType>>(null);

  const defer = useCallback(() => {
    if (deferRef.current) {
      console.warn(
        'cannot create two instances of deferred promise, ensure the first is resolved before creating a new one'
      );

      return null;
    }

    const deferred = {} as DeferredPromise<DeferType>;

    const promise = new Promise<DeferType>((resolve, reject) => {
      // these resolve / reject wrapper functions make sure that we clean up the
      // ref after the promise has been fulfilled
      const deferredResolve: typeof resolve = (r) => {
        resolve(r);
        deferRef.current = null;
      };

      const deferredReject: typeof reject = (e) => {
        reject(e);
        deferRef.current = null;
      };

      deferred.resolve = deferredResolve;
      deferred.reject = deferredReject;
    });

    deferred.promise = promise;
    deferRef.current = deferred;

    return deferRef.current;
  }, []);

  useEffect(() => {
    return () => {
      // ensure that if the promise has not been fulfilled when the component unmounts, we do cleanup
      if (deferRef.current) {
        deferRef.current?.reject('Component unmounted');
      }
    };
  }, []);

  return { defer, deferRef };
}
